/**
 * Composable for building image URLs from API attachment data or static paths.
 *
 * Two CDN origins are used:
 *  - `imageUrl`  (IMAGE_URL)        → static site images at /Images/...
 *  - `apiBase`   (API_BASE / AND_DINE_IMAGE) → API-served uploads (attachments)
 *
 * API attachments come as arrays of objects with `file_path_*` properties.
 * This composable normalises those into full URLs.
 */
export function useImageUrl() {
  const config = useRuntimeConfig();
  const imageBase = computed(
    () => (config.public.imageUrl as string) || "https://image.anddine.co.uk",
  );
  const apiBase = computed(() => (config.public.apiBase as string) || "");

  /**
   * Build a full URL for a static site image.
   * Usage: `staticUrl('/Images/b-roll/login.jpg')`
   */
  function staticUrl(path: string): string {
    if (!path) return "";
    if (path.startsWith("http")) return path;
    const cleanPath = path.startsWith("/") ? path : `/${path}`;
    return `${imageBase.value}${cleanPath}`;
  }

  /**
   * Build a full URL from an API attachment object or array.
   *
   * The API returns attachments as arrays of objects like:
   *   `[{ file_path_small: './uploads/...', file_path_big: '...', file_path_banner: '...' }]`
   *
   * Or the store may have already transformed it to a string path.
   *
   * @param attachment - The raw attachment value (array, object, or string)
   * @param size - Which file_path variant to use: 'small' | 'big' | 'banner' | 'original'
   * @param fallback - Optional fallback static image path
   */
  function attachmentUrl(
    attachment: unknown,
    size: "small" | "medium" | "big" | "banner" | "original" = "big",
    fallback = "/Images/site-images/placeholder.png",
  ): string {
    const filePath = extractFilePath(attachment, size);

    if (filePath) {
      if (filePath.startsWith("http")) return filePath;
      const cleaned = filePath.replace(/^\.\//, "");
      // These URLs get embedded in CSS `background-image: url(...)` (quoted and
      // unquoted). `encodeURI` leaves several chars unescaped that break that:
      //   '        closes a single-quoted url('...') early  (e.g. Dorothy's_Deli.jpg)
      //   ( )      break an unquoted url(...) token
      //   # ?      truncate the request path/fragment for every consumer
      // Percent-encode them so the result is safe in any url() context.
      return `${apiBase.value}/${encodeURI(cleaned).replace(
        /['()#?]/g,
        (c) => "%" + c.charCodeAt(0).toString(16).toUpperCase(),
      )}`;
    }

    return fallback ? staticUrl(fallback) : "";
  }

  /**
   * Extract the file path string from various attachment data shapes.
   */
  function extractFilePath(
    attachment: unknown,
    size: "small" | "medium" | "big" | "banner" | "original",
  ): string {
    if (!attachment) return "";

    // Already a full URL string
    if (typeof attachment === "string") {
      if (
        attachment.startsWith("http") ||
        attachment.startsWith("./") ||
        attachment.startsWith("/")
      ) {
        return attachment;
      }
      return attachment;
    }

    // Array of attachment objects — take the first one
    if (Array.isArray(attachment)) {
      if (attachment.length === 0) return "";
      return extractFilePath(attachment[0], size);
    }

    // Object with file_path_* properties
    if (typeof attachment === "object" && attachment !== null) {
      const obj = attachment as Record<string, unknown>;
      const sizeKey = `file_path_${size}`;

      // Try the requested size first, then fall back through alternatives
      const candidates = [
        sizeKey,
        "file_path_big",
        "file_path_medium",
        "file_path_small",
        "file_path_banner",
        "file_path_original",
        "file_path",
        "url",
        "src",
      ];

      for (const key of candidates) {
        if (obj[key] && typeof obj[key] === "string") {
          return obj[key] as string;
        }
      }
    }

    return "";
  }

  return {
    staticUrl,
    attachmentUrl,
    imageBase,
    apiBase,
  };
}
